import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import moment, { Moment } from 'moment';
import { interval, Subscription } from 'rxjs';
import {
  ApplicationKybEntityRun,
  ApplicationKybQueueSummary,
  ApplicationKybReevaluationItem,
  ApplicationKybRunFilters,
  ApplicationKybRunHistoryItem,
  RetryApplicationKybRunsResult,
  isAdmin,
  isInternalUser,
  KybApplicationStateFilter,
  KybAttentionReason,
  KybQueueView,
  KybReconciliationStatus,
  KybReevaluationStatus,
  KybResultStatus,
  MAX_KYB_RUN_RETRY_BATCH,
  kybDisplayStatus,
} from '@portal-workspace/grow-shared-library';
import {
  CustomContentLoaderComponent,
  CustomPaginatorComponent,
  getUser,
  PortalHotToastService,
} from '@portal-workspace/grow-ui-library';
import { ApplicationService } from '../../../service/application.service';
import { navigationUrlForApplication } from '../../../service/navigation-urls';

const AUTO_REFRESH_MS = 30_000;

/** Value backing the "All" row. Stripped before the filters reach the API. */
const ALL_STATUSES = 'ALL';
type StatusFilterValue = KybResultStatus | typeof ALL_STATUSES;
type ReconciliationFilterValue = 'ALL' | 'PENDING' | 'RECONCILED';
type QueueSelection = 'ACTIONS' | 'RECONCILIATION' | 'FAILED' | 'STALLED' | 'MONITORING' | 'ALL';
type QueueSort = 'PRIORITY' | 'OLDEST' | 'LATEST';
type SummaryCountKey = keyof ApplicationKybQueueSummary;
type RetryFeedback = {
  state: 'progress' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  actionLabel: string | null;
  searchApplicationId: number | null;
};

@Component({
  selector: 'kyb-run-list',
  templateUrl: './kyb-run-list.component.html',
  styleUrls: ['./kyb-run-list.component.scss'],
  standalone: true,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatTableModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    FormsModule,
    NgClass,
    RouterLink,
    CustomContentLoaderComponent,
    CustomPaginatorComponent,
  ],
  // The native adapter reads and writes the browser locale's m/d/yyyy, which also mis-parses a
  // typed 04/08/2026 as 8 April. Matches the adapter wiring in reconciliation-sync.component.ts.
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'DD/MM/YYYY',
        },
        display: {
          dateInput: 'DD/MM/YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
})
export class KybRunListComponent implements OnInit, OnDestroy {

  displayedColumns: string[] = [
    'select',
    'applicationId',
    'legalName',
    'reason',
    'status',
    'lastUpdate',
    'actions',
  ];

  selection = new SelectionModel<ApplicationKybEntityRun>(true, []);
  maxBatch = MAX_KYB_RUN_RETRY_BATCH;

  dataSource: ApplicationKybEntityRun[] = [];
  summary: ApplicationKybQueueSummary = {
    actionRequired: 0,
    reconciliationRequired: 0,
    failed: 0,
    stalled: 0,
    monitoring: 0,
    all: 0,
  };
  total = 0;
  limit = 20;
  offset = 0;
  expandedEntityId: number | null = null;
  isLoading = false;
  isActionRunning = false;
  refreshFailed = false;
  hasFilters = false;
  lastRefreshedAt: Moment | null = null;
  retryFeedback: RetryFeedback | null = null;

  queueView: KybQueueView = 'ACTION_REQUIRED';
  attentionReasons: KybAttentionReason[] = [];
  activeQueueSelection: QueueSelection = 'ACTIONS';
  sortMode: QueueSort = 'PRIORITY';

  readonly queueSelectors: {
    value: QueueSelection;
    label: string;
    count: SummaryCountKey;
  }[] = [
    {value: 'ACTIONS', label: 'Action required', count: 'actionRequired'},
    {value: 'RECONCILIATION', label: 'Reconciliation', count: 'reconciliationRequired'},
    {value: 'FAILED', label: 'Failed', count: 'failed'},
    {value: 'STALLED', label: 'Stalled', count: 'stalled'},
    {value: 'MONITORING', label: 'Monitoring', count: 'monitoring'},
    {value: 'ALL', label: 'All activity', count: 'all'},
  ];

  readonly allStatuses = ALL_STATUSES;
  statusFilter: StatusFilterValue[] = [ALL_STATUSES];
  // (selectionChange) reports the new selection but not which row was clicked, and "All" versus a
  // specific status has to be resolved in favour of whichever the user just added.
  private lastStatusFilter: StatusFilterValue[] = [ALL_STATUSES];
  searchFilter = '';
  startDate: Moment | null = null;
  endDate: Moment | null = null;
  reconciliationFilter: ReconciliationFilterValue = 'ALL';
  applicationStateFilter: KybApplicationStateFilter = 'SUBMITTED';

  readonly loggedInUser = getUser();
  readonly canRecoverRuns = isAdmin(this.loggedInUser);
  readonly canReevaluateRisk = isInternalUser(this.loggedInUser);
  private refreshSubscription?: Subscription;
  private retryingRunIds = new Set<number>();

  statusOptions: { value: KybResultStatus, label: string }[] = [
    { value: 'INITIATING', label: 'Initiating' },
    { value: 'RETRY_PENDING', label: 'Retry Pending' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'CALLBACK_RECEIVED', label: 'Callback Received' },
    { value: 'RESULT_RETRY_PENDING', label: 'Result Retry Pending' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'SUPERSEDED', label: 'Superseded' },
    { value: 'INITIATION_FAILED', label: 'Initiation Failed' },
    { value: 'RESULT_PROCESSING_FAILED', label: 'Result Processing Failed' },
  ];

  constructor(private applicationService: ApplicationService,
              private toastService: PortalHotToastService,
              private route: ActivatedRoute,
              private router: Router) {
  }

  ngOnInit(): void {
    this.hydrateFromQueryParams();
    this.loadData();
    this.refreshSubscription = interval(AUTO_REFRESH_MS).subscribe(() => {
      if (this.canAutoRefresh()) {
        this.loadData(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  private selectedStatuses(): KybResultStatus[] {
    return this.statusFilter.filter((value): value is KybResultStatus => value !== ALL_STATUSES);
  }

  /**
   * "All" and specific statuses are mutually exclusive, and an empty selection has no meaning, so
   * whichever side was just added wins and unticking the last status falls back to "All".
   */
  onStatusFilterChange(): void {
    const justAddedAll = this.statusFilter.includes(ALL_STATUSES)
      && !this.lastStatusFilter.includes(ALL_STATUSES);
    const statuses = this.selectedStatuses();

    this.statusFilter = justAddedAll || !statuses.length ? [ALL_STATUSES] : statuses;
    this.lastStatusFilter = [...this.statusFilter];
  }

  selectQueue(selection: QueueSelection): void {
    this.retryFeedback = null;
    this.activeQueueSelection = selection;
    this.attentionReasons = [];

    switch (selection) {
      case 'RECONCILIATION':
        this.queueView = 'ACTION_REQUIRED';
        this.attentionReasons = ['RECONCILIATION_REQUIRED'];
        break;
      case 'FAILED':
        this.queueView = 'ACTION_REQUIRED';
        this.attentionReasons = ['RUN_FAILED', 'REEVALUATION_FAILED'];
        break;
      case 'STALLED':
        this.queueView = 'ACTION_REQUIRED';
        this.attentionReasons = ['RUN_STALLED', 'REEVALUATION_STALLED'];
        break;
      case 'MONITORING':
        this.queueView = 'MONITORING';
        break;
      case 'ALL':
        this.queueView = 'ALL';
        break;
      default:
        this.queueView = 'ACTION_REQUIRED';
    }

    this.offset = 0;
    this.expandedEntityId = null;
    this.selection.clear();
    this.syncQueryParams();
    this.loadData();
  }

  queueCount(key: SummaryCountKey): number {
    return this.summary[key];
  }

  loadData(background = false): void {
    if (!background) {
      this.isLoading = true;
      // Selection is by object identity and the rows are about to be replaced.
      this.selection.clear();
    }
    this.updateFiltersStatus();

    const statuses = this.selectedStatuses();
    const reconciliationStatuses: KybReconciliationStatus[] | undefined =
      this.reconciliationFilter === 'PENDING'
        ? ['REVIEW_REQUIRED']
        : this.reconciliationFilter === 'RECONCILED'
          ? ['RECONCILED']
          : undefined;

    const filters: ApplicationKybRunFilters = {
      queueView: this.queueView,
      applicationState: this.applicationStateFilter,
      attentionReasons: this.attentionReasons.length ? this.attentionReasons : undefined,
      statuses: statuses.length ? statuses : undefined,
      reconciliationStatuses,
      search: this.searchFilter.trim() || undefined,
      // createdAt is stored in UTC, so the picked local day is sent as the UTC instants that
      // bracket it. Without this an AEST operator loses the first 10 hours of each day.
      startDate: this.startDate ? moment(this.startDate).startOf('day').toISOString() : undefined,
      endDate: this.endDate ? moment(this.endDate).endOf('day').toISOString() : undefined,
    };

    this.applicationService.getApplicationKybRunsFn({
      page: { limit: this.limit, offset: this.offset },
      filter: '',
      sorts: this.sortsForRequest(),
      filters,
    }).subscribe({
      next: (response) => {
        this.dataSource = response.payload;
        this.total = response.total;
        this.summary = response.summary;
        this.lastRefreshedAt = moment();
        this.refreshFailed = false;
        this.isLoading = false;
      },
      error: () => {
        if (background) {
          this.refreshFailed = true;
        } else {
          this.toastService.error('Unable to load the KYB queue', 'Error');
        }
        this.isLoading = false;
      },
    });
  }

  onPagination(event: PageEvent): void {
    this.offset = event.pageIndex;
    this.limit = event.pageSize;
    this.syncQueryParams();
    this.loadData();
  }

  onSortModeChange(): void {
    this.offset = 0;
    this.syncQueryParams();
    this.loadData();
  }

  applyFilters(): void {
    this.offset = 0;
    this.syncQueryParams();
    this.loadData();
  }

  clearFilters(): void {
    this.statusFilter = [ALL_STATUSES];
    this.lastStatusFilter = [ALL_STATUSES];
    this.searchFilter = '';
    this.startDate = null;
    this.endDate = null;
    this.reconciliationFilter = 'ALL';
    this.applicationStateFilter = 'SUBMITTED';
    this.offset = 0;
    this.syncQueryParams();
    this.loadData();
  }

  manualRefresh(): void {
    this.loadData();
  }

  lastRefreshedLabel(): string {
    if (this.refreshFailed) {
      return 'Refresh failed';
    }
    return this.lastRefreshedAt
      ? `Last updated ${this.lastRefreshedAt.format('HH:mm:ss')}`
      : '';
  }

  activeFilterCount(): number {
    return Number(this.selectedStatuses().length > 0)
      + Number(this.searchFilter.trim() !== '')
      + Number(this.startDate !== null)
      + Number(this.endDate !== null)
      + Number(this.reconciliationFilter !== 'ALL')
      + Number(this.applicationStateFilter !== 'SUBMITTED');
  }

  private updateFiltersStatus(): void {
    this.hasFilters = this.activeFilterCount() > 0;
  }

  private sortsForRequest(): {prop: string, dir: 'asc' | 'desc'}[] | null {
    if (this.sortMode === 'PRIORITY' && this.queueView === 'ACTION_REQUIRED') {
      return null;
    }
    if (this.sortMode === 'OLDEST') {
      return [{
        prop: this.queueView === 'ACTION_REQUIRED' ? 'attentionSince' : 'createdAt',
        dir: 'asc',
      }];
    }
    return [{prop: 'lastUpdate', dir: 'desc'}];
  }

  private canAutoRefresh(): boolean {
    return this.queueView !== 'ALL'
      && document.visibilityState === 'visible'
      && !this.isLoading
      && !this.isActionRunning
      && !this.selection.hasValue()
      && this.expandedEntityId === null;
  }

  private hydrateFromQueryParams(): void {
    const params = this.route.snapshot.queryParamMap;
    const view = params.get('view');
    if (view === 'ACTION_REQUIRED' || view === 'MONITORING' || view === 'ALL') {
      this.queueView = view;
    }

    const allowedReasons: KybAttentionReason[] = [
      'RECONCILIATION_REQUIRED',
      'RUN_FAILED',
      'RUN_STALLED',
      'REEVALUATION_FAILED',
      'REEVALUATION_STALLED',
    ];
    this.attentionReasons = (params.get('reasons') ?? '')
      .split(',')
      .filter((reason): reason is KybAttentionReason =>
        allowedReasons.includes(reason as KybAttentionReason));
    this.searchFilter = params.get('search') ?? '';
    const applicationState = params.get('applicationState');
    if (applicationState === 'SUBMITTED' || applicationState === 'DRAFT' || applicationState === 'ALL') {
      this.applicationStateFilter = applicationState;
    }
    const sort = params.get('sort');
    if (sort === 'PRIORITY' || sort === 'OLDEST' || sort === 'LATEST') {
      this.sortMode = sort;
    }
    this.offset = Math.max(0, Number(params.get('page')) || 0);
    this.activeQueueSelection = this.selectionForCurrentView();
  }

  private selectionForCurrentView(): QueueSelection {
    if (this.queueView === 'MONITORING') return 'MONITORING';
    if (this.queueView === 'ALL') return 'ALL';
    if (this.attentionReasons.length === 1
      && this.attentionReasons[0] === 'RECONCILIATION_REQUIRED') return 'RECONCILIATION';
    if (this.attentionReasons.includes('RUN_FAILED')) return 'FAILED';
    if (this.attentionReasons.includes('RUN_STALLED')) return 'STALLED';
    return 'ACTIONS';
  }

  private syncQueryParams(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        view: this.queueView,
        reasons: this.attentionReasons.length ? this.attentionReasons.join(',') : null,
        search: this.searchFilter.trim() || null,
        applicationState: this.applicationStateFilter === 'SUBMITTED' ? null : this.applicationStateFilter,
        sort: this.sortMode,
        page: this.offset || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  toggleExpanded(element: ApplicationKybEntityRun): void {
    this.expandedEntityId = this.expandedEntityId === element.applicationCommercialEntityId
      ? null
      : element.applicationCommercialEntityId;
  }

  isExpanded(element: ApplicationKybEntityRun): boolean {
    return this.expandedEntityId === element.applicationCommercialEntityId;
  }

  applicationLink(element: ApplicationKybEntityRun): string[] {
    return navigationUrlForApplication(String(element.applicationId));
  }

  reconciliationLink(element: ApplicationKybEntityRun): string[] {
    return navigationUrlForApplication(String(element.applicationId), 'kyc-aml');
  }

  diagnosticsId(element: ApplicationKybEntityRun): string {
    return `kyb-diagnostics-${element.applicationCommercialEntityId}`;
  }

  reasonLabel(reason: KybAttentionReason): string {
    switch (reason) {
      case 'RECONCILIATION_REQUIRED': return 'Reconciliation required';
      case 'RUN_FAILED': return 'KYB run failed';
      case 'RUN_STALLED': return 'KYB run stalled';
      case 'REEVALUATION_FAILED': return 'Risk re-evaluation failed';
      case 'REEVALUATION_STALLED': return 'Risk re-evaluation stalled';
    }
  }

  reasonsLabel(element: ApplicationKybEntityRun): string {
    if (!element.attentionReasons.length) {
      return '-';
    }
    return element.attentionReasons.map(reason => this.reasonLabel(reason)).join(', ');
  }

  hasReason(element: ApplicationKybEntityRun, reasons: KybAttentionReason[]): boolean {
    return reasons.some(reason => element.attentionReasons.includes(reason));
  }

  needsRunRecovery(element: ApplicationKybEntityRun): boolean {
    return this.hasReason(element, ['RUN_FAILED', 'RUN_STALLED']);
  }

  needsReevaluationRecovery(element: ApplicationKybEntityRun): boolean {
    return this.hasReason(element, ['REEVALUATION_FAILED', 'REEVALUATION_STALLED']);
  }

  needsReconciliation(element: ApplicationKybEntityRun): boolean {
    return element.attentionReasons.includes('RECONCILIATION_REQUIRED');
  }

  /** Reuses the shared pending/resolved/failed collapse so status colours match the rest of the portal. */
  statusClass(status: KybResultStatus): string {
    return `status-${kybDisplayStatus(status)}`;
  }

  statusLabel(status: KybResultStatus): string {
    return this.statusOptions.find(option => option.value === status)?.label ?? status;
  }

  reconciliationStatusLabel(status: KybReconciliationStatus | null): string {
    switch (status) {
      case 'REVIEW_REQUIRED': return 'Pending review';
      case 'RECONCILED': return 'Reconciled';
      default: return '-';
    }
  }

  /** Material joins every selected label, which overflows the field; show a count past the first. */
  statusTriggerLabel(): string {
    const [first, ...rest] = this.selectedStatuses();
    if (!first) {
      return 'All';
    }
    return rest.length ? `${this.statusLabel(first)} (+${rest.length})` : this.statusLabel(first);
  }

  businessNumber(element: ApplicationKybEntityRun): string {
    return element.abn || element.acn || '-';
  }

  /** Prefixed for the expander, where there is no column header to lean on. */
  businessNumberDetail(element: ApplicationKybEntityRun): string {
    if (element.abn) {
      return `ABN ${element.abn}`;
    }
    if (element.acn) {
      return `ACN ${element.acn}`;
    }
    return '-';
  }

  /** Entity roles, so a secondary entity is identifiable once KYB widens beyond the primary. */
  entityRoles(element: ApplicationKybEntityRun): string {
    const roles: string[] = [];
    if (element.isPrimary) roles.push('Primary');
    if (element.isBorrower) roles.push('Borrower');
    if (element.isGuarantor) roles.push('Guarantor');
    return roles.join(', ') || '-';
  }

  attempts(run: ApplicationKybRunHistoryItem): string {
    return `${run.initiationAttemptCount} / ${run.resultProcessingAttemptCount}`;
  }

  /** Shared by runs and re-evaluations; both carry the same nullable name. */
  triggeredBy(item: { triggeredByName: string | null }): string {
    return item.triggeredByName ?? 'System';
  }

  formatDate(value: string | null): string {
    if (!value) {
      return '-';
    }
    return moment(value).format('DD/MM/YYYY HH:mm');
  }

  /**
   * Anything short of completed can be retried: the endpoint fetches the result first and only
   * starts a new workflow if there is nothing to fetch, so retrying an in-flight run is safe.
   */
  isRetryable(element: ApplicationKybEntityRun): boolean {
    return element.latestRun.status !== 'COMPLETED';
  }

  isSelectable(element: ApplicationKybEntityRun): boolean {
    return this.canRecoverRuns && this.isRetryable(element);
  }

  private retryableRows(): ApplicationKybEntityRun[] {
    return this.dataSource.filter(row => this.isSelectable(row));
  }

  /** "All" means all retryable rows on the page; completed rows have no checkbox to tick. */
  isAllSelected(): boolean {
    const retryable = this.retryableRows();
    return retryable.length > 0 && retryable.every(row => this.selection.isSelected(row));
  }

  hasPartialSelection(): boolean {
    return this.selection.hasValue() && !this.isAllSelected();
  }

  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.retryableRows());
  }

  toggleRow(element: ApplicationKybEntityRun): void {
    this.selection.toggle(element);
  }

  selectedCount(): number {
    return this.selection.selected.length;
  }

  /** The endpoint caps a batch, so an oversized selection is blocked before the round trip. */
  isSelectionOverLimit(): boolean {
    return this.selectedCount() > this.maxBatch;
  }

  retrySelected(): void {
    this.retryRows(this.selection.selected);
  }

  recoverRun(element: ApplicationKybEntityRun): void {
    this.retryRows([element]);
  }

  isRunRetrying(element: ApplicationKybEntityRun): boolean {
    return this.retryingRunIds.has(element.latestRun.id);
  }

  dismissRetryFeedback(): void {
    this.retryFeedback = null;
  }

  openRetryResults(): void {
    if (!this.retryFeedback?.actionLabel) {
      return;
    }
    this.statusFilter = [ALL_STATUSES];
    this.lastStatusFilter = [ALL_STATUSES];
    this.searchFilter = this.retryFeedback.searchApplicationId?.toString() ?? '';
    this.startDate = null;
    this.endDate = null;
    this.reconciliationFilter = 'ALL';
    this.applicationStateFilter = 'ALL';
    this.sortMode = 'LATEST';
    this.selectQueue('ALL');
  }

  private retryRows(rows: ApplicationKybEntityRun[]): void {
    const ids = rows.map(row => row.latestRun.id);
    if (!ids.length || !this.canRecoverRuns) {
      return;
    }
    if (ids.length > this.maxBatch) {
      this.toastService.error(`Select at most ${this.maxBatch} runs to retry at once`, 'Too many selected');
      return;
    }

    this.isActionRunning = true;
    this.retryingRunIds = new Set(ids);
    this.retryFeedback = {
      state: 'progress',
      title: ids.length === 1 ? 'Retrying KYB run' : `Retrying ${ids.length} KYB runs`,
      message: 'Checking FrankieOne for an existing result. If none is available, a new workflow will be started.',
      actionLabel: null,
      searchApplicationId: null,
    };
    this.applicationService.retryApplicationKybRunsFn(ids).subscribe({
      next: (response) => {
        this.isActionRunning = false;
        this.retryingRunIds.clear();
        this.retryFeedback = this.completedRetryFeedback(response.payload, rows);
        this.selection.clear();
        this.loadData(true);
      },
      error: (error) => {
        this.isActionRunning = false;
        this.retryingRunIds.clear();
        const message = error?.error?.message || 'Unable to retry the selected KYB runs';
        this.retryFeedback = {
          state: 'error',
          title: 'KYB retry failed',
          message: `The selected runs remain in this queue and can be retried. ${message}`,
          actionLabel: null,
          searchApplicationId: null,
        };
        this.toastService.error(message, 'Retry failed');
      },
    });
  }

  private completedRetryFeedback(
    result: RetryApplicationKybRunsResult,
    rows: ApplicationKybEntityRun[],
  ): RetryFeedback {
    const requestedCount = rows.length;
    const initiatedCount = result.succeeded.filter(item => item.action === 'initiated').length;
    const recoveredCount = result.succeeded.filter(item => item.action === 'result-reprocessed').length;
    const failedCount = result.failed.length;
    const searchApplicationId = requestedCount === 1 ? rows[0].applicationId : null;
    const actionLabel = requestedCount === 1 ? 'View run' : 'View in All activity';

    if (requestedCount === 1 && initiatedCount === 1) {
      return {
        state: 'success',
        title: 'New KYB workflow started',
        message: 'No completed result was available, so the retry is now in progress. Open All activity to see its latest status.',
        actionLabel,
        searchApplicationId,
      };
    }
    if (requestedCount === 1 && recoveredCount === 1) {
      return {
        state: 'success',
        title: 'KYB result recovered',
        message: 'The existing FrankieOne result was retrieved and saved. Open All activity to view the updated run.',
        actionLabel,
        searchApplicationId,
      };
    }

    const outcomes: string[] = [];
    if (initiatedCount) {
      outcomes.push(`${initiatedCount} new ${initiatedCount === 1 ? 'workflow' : 'workflows'} started`);
    }
    if (recoveredCount) {
      outcomes.push(`${recoveredCount} existing ${recoveredCount === 1 ? 'result' : 'results'} recovered`);
    }
    if (failedCount) {
      outcomes.push(`${failedCount} ${failedCount === 1 ? 'run' : 'runs'} could not be retried`);
    }

    const message = `${outcomes.join('; ')}. `
      + (initiatedCount ? 'New workflows are in progress. ' : '')
      + (recoveredCount ? 'Recovered runs no longer need attention. ' : '')
      + (failedCount
        ? `Runs that failed remain in this queue. First failure: ${result.failed[0].message}`
        : 'Open All activity to view the updated runs.');

    return {
      state: failedCount ? 'warning' : 'success',
      title: failedCount
        ? `Retried ${result.succeeded.length} of ${requestedCount} KYB runs`
        : `${requestedCount} KYB runs retried`,
      message,
      actionLabel,
      searchApplicationId,
    };
  }

  reevaluationStatusLabel(status: KybReevaluationStatus): string {
    switch (status) {
      case 'IN_PROGRESS': return 'Awaiting response';
      case 'RETRY_PENDING': return 'Retry pending';
      case 'COMPLETED': return 'Completed';
      case 'FAILED': return 'Failed';
      default: return status;
    }
  }

  /** Reuses the run status colours: a re-evaluation awaiting its callback reads as pending. */
  reevaluationStatusClass(status: KybReevaluationStatus): string {
    switch (status) {
      case 'COMPLETED': return 'status-resolved';
      case 'FAILED': return 'status-failed';
      default: return 'status-pending';
    }
  }

  reevaluationTriggerLabel(reevaluation: ApplicationKybReevaluationItem): string {
    switch (reevaluation.triggerReason) {
      case 'submit': return 'Application submitted';
      case 'kyb-complete': return 'KYB completed';
      case 'anzsic-edit': return 'ANZSIC edited';
      case 'entity-risk-changed': return 'Key person risk changed';
      case 'manual-retry': return 'Manual';
      default: return reevaluation.triggerReason;
    }
  }

  /** Blank until the callback lands, which is the point: a long-blank row is a stalled re-evaluation. */
  reevaluationResponseTime(reevaluation: ApplicationKybReevaluationItem): string {
    if (reevaluation.durationMs == null) {
      return '-';
    }
    const seconds = reevaluation.durationMs / 1000;
    return seconds < 60
      ? `${seconds.toFixed(1)}s`
      : `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  }

  reevaluationRisk(reevaluation: ApplicationKybReevaluationItem): string {
    if (!reevaluation.riskLevel && reevaluation.riskScore == null) {
      return '-';
    }
    return [reevaluation.riskLevel, reevaluation.riskScore].filter(part => part != null).join(' / ');
  }

  /**
   * Re-evaluation only patches custom attributes and re-scores check results the KYB run already
   * produced, so unlike the KYB retry it cannot spend a workflow execution and is offered on any run
   * that completed. The backend still enforces the submitted / completed-KYB / ANZSIC preconditions.
   */
  canReevaluate(element: ApplicationKybEntityRun): boolean {
    return this.canReevaluateRisk && element.latestRun.status === 'COMPLETED';
  }

  retryReevaluation(element: ApplicationKybEntityRun): void {
    if (!this.canReevaluate(element)) {
      return;
    }
    this.isActionRunning = true;
    this.applicationService.retryKybReevaluationFn(element.latestRun.id).pipe(
      this.toastService.spinnerObservable(),
    ).subscribe({
      next: (response) => {
        this.isActionRunning = false;
        this.toastService.success(response.message || 'Risk re-evaluation sent', 'Success');
        this.loadData();
      },
      error: (error) => {
        this.isActionRunning = false;
        this.toastService.error(error?.error?.message || 'Unable to send the risk re-evaluation', 'Error');
        this.loadData();
      },
    });
  }

}
